import React, { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { Bug, Loader2, Camera } from 'lucide-react';
import AnnotationCanvas from './AnnotationCanvas';
import BugReportModal from './BugReportModal';

/**
 * ScreenshotCapture — Floating report button available on every authenticated page.
 * Flow: idle → capturing → annotating → form → idle
 *
 * Mount in AppLayout to make globally available.
 */
export default function ScreenshotCapture() {
    const [state, setState] = useState('idle'); // idle | capturing | annotating | form
    const [rawScreenshot, setRawScreenshot] = useState(null);
    const [annotatedScreenshot, setAnnotatedScreenshot] = useState(null);
    const [hovered, setHovered] = useState(false);
    const [capturedPageUrl, setCapturedPageUrl] = useState('');

    const capture = useCallback(async () => {
        setState('capturing');
        setCapturedPageUrl(window.location.href);
        try {
            // Small delay to let the button hide
            await new Promise(r => setTimeout(r, 100));

            const canvas = await html2canvas(document.body, {
                useCORS: true,
                scale: 1,
                logging: false,
                backgroundColor: '#FAFAF5',
                ignoreElements: (el) => {
                    // Ignore the floating capture button itself
                    return el.hasAttribute?.('data-screenshot-capture');
                },
            });

            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            setRawScreenshot(dataUrl);
            setState('annotating');
        } catch (err) {
            console.error('Screenshot capture failed:', err);
            setState('idle');
        }
    }, []);

    const handleAnnotationComplete = (annotatedDataUrl) => {
        setAnnotatedScreenshot(annotatedDataUrl);
        setState('form');
    };

    const handleAnnotationCancel = () => {
        setRawScreenshot(null);
        setState('idle');
    };

    const handleFormClose = () => {
        setRawScreenshot(null);
        setAnnotatedScreenshot(null);
        setState('idle');
    };

    // Don't render button during capture or annotation
    if (state === 'capturing') {
        return (
            <div data-screenshot-capture="true" className="fixed bottom-20 right-6 z-50">
                <div className="w-12 h-12 rounded-full bg-stone-900 text-white flex items-center justify-center shadow-lg">
                    <Loader2 className="w-5 h-5 animate-spin" />
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Floating button — hidden during annotation */}
            {state === 'idle' && (
                <div data-screenshot-capture="true"
                    className="fixed bottom-20 right-6 z-50"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}>
                    <button onClick={capture}
                        className="group flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all px-4 py-3"
                        title="Report Bug / Request Feature">
                        <Bug className="w-5 h-5" />
                        <span className={`text-sm font-medium overflow-hidden transition-all ${hovered ? 'max-w-[120px] opacity-100' : 'max-w-0 opacity-0'}`}>
                            Report
                        </span>
                    </button>
                </div>
            )}

            {/* Annotation overlay */}
            {state === 'annotating' && rawScreenshot && (
                <AnnotationCanvas
                    screenshotDataUrl={rawScreenshot}
                    onComplete={handleAnnotationComplete}
                    onCancel={handleAnnotationCancel}
                />
            )}

            {/* Bug report form */}
            {state === 'form' && (
                <BugReportModal
                    open={true}
                    onClose={handleFormClose}
                    screenshotDataUrl={annotatedScreenshot || rawScreenshot}
                    pageUrl={capturedPageUrl}
                />
            )}
        </>
    );
}
